import { type QuoteDestination, SHIPPING_REGION_LABELS } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { formatCount, formatWon } from '@/shared/lib';
import { Input } from '@/shared/ui';

export interface QuoteShippingLineRowProps {
  readonly destination: QuoteDestination;
  /** 0부터 — 화면 번호는 index + 1. */
  readonly index: number;
  readonly unitPrice: string;
  /** 표시용 로컬 계산 금액. 0 이상 정수가 아니면 null. */
  readonly amount: number | null;
  /** 서버에 이 배송지의 배송비 줄이 아직 없다(추천값으로 채운 행). */
  readonly unsaved: boolean;
  readonly error?: string;
  readonly disabled?: boolean;
  readonly onChange: (unitPrice: string) => void;
}

const CELL_CLASS = 'px-2 py-2 align-top';

/**
 * 배송비 한 행(SUPER-86 §6-2) — 배송지 요약·지역·금액 입력. 줄 이름·수량(1)은 고치지 않고 추가·삭제·순서 이동도 없다
 * (배송지마다 정확히 하나, 배송지·수량 변경은 새 견적 요청 — D12). 지역을 알 수 없는 배송지(region null)는 '-'.
 */
export const QuoteShippingLineRow = ({
  destination,
  index,
  unitPrice,
  amount,
  unsaved,
  error,
  disabled = false,
  onChange,
}: QuoteShippingLineRowProps): ReactElement => {
  const number = index + 1;
  const recipient = destination.recipientName ?? '받는 분은 주문 때 입력';

  return (
    <tr className="border-t">
      <td className={CELL_CLASS}>
        <p className="font-medium">
          {number}. {recipient} · {formatCount(destination.quantity)}개
        </p>
        <p className="text-xs text-muted-foreground">
          ({destination.postalCode}) {destination.address1}
        </p>
      </td>
      <td className={CELL_CLASS}>
        {destination.region === null ? '-' : SHIPPING_REGION_LABELS[destination.region]}
      </td>
      <td className={CELL_CLASS}>
        <Input
          aria-label={`${number}번 배송지 배송비`}
          inputMode="numeric"
          value={unitPrice}
          hasError={Boolean(error)}
          disabled={disabled}
          className="text-right"
          onChange={(event) => onChange(event.target.value)}
        />
        {error ? (
          <p role="alert" className="mt-1 text-xs text-destructive">
            {error}
          </p>
        ) : null}
      </td>
      <td className={`${CELL_CLASS} text-right tabular-nums`}>
        {amount === null ? '-' : formatWon(amount)}
        {unsaved ? (
          <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-800">
            저장 안 됨
          </span>
        ) : null}
      </td>
    </tr>
  );
};
